/*
 * ThresholdMgmt.tsx — S57: Threshold Management (CFO-level)
 * API: GET /api/v1/thresholds/dashboard
 */
import { useState, useEffect } from 'react';
import { API_V1, formatAmount, safeFetch } from '../constants';

interface ThresholdDash {
  threshold_summary: any[];
  pending_suggestions: any[];
  pending_count: number;
  recent_decisions: any[];
}

interface Threshold {
  id: number;
  threshold_type: string;
  current_value: number;
  min_allowed: number;
  max_allowed: number;
  set_by: string;
  set_at: string;
}

interface Suggestion {
  id: number;
  threshold_type: string;
  suggested_value: number;
  current_value: number;
  reason: string;
  status: string;
  evidence: Record<string, any>;
}

export default function ThresholdMgmt() {
  const [dashboard, setDashboard] = useState<ThresholdDash | null>(null);
  const [thresholds, setThresholds] = useState<Threshold[]>([]);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      safeFetch(`${API_V1}/thresholds/dashboard`, null),
      safeFetch(`${API_V1}/thresholds`, { thresholds: [] }),
      safeFetch(`${API_V1}/thresholds/suggestions?status=PENDING`, { suggestions: [] }),
    ])
      .then(([dash, thr, sug]) => {
        setDashboard(dash);
        setThresholds(thr.thresholds || []);
        setSuggestions(sug.suggestions || []);
        setLoading(false);
      })
      .catch(e => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="main" style={{ padding: 32 }}><p style={{ color: 'var(--text-secondary)' }}>Loading threshold management...</p></div>;
  if (error) return (
    <div className="main" style={{ padding: 32 }}>
      <p style={{ color: 'var(--red)' }}>Error: {error}</p>
      <button onClick={load} style={{ marginTop: 8 }}>Retry</button>
    </div>
  );

  const pending = suggestions.filter(s => s.status === 'PENDING');

  return (
    <div className="main" style={{ padding: 32, maxWidth: 900 }}>
      <h2 className="page-title">Threshold Management</h2>

      {pending.length > 0 && (
        <div className="card" style={{ padding: 20, marginBottom: 24, border: '1px solid var(--border-subtle)', borderRadius: 8 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--amber)', marginBottom: 12 }}>
            {pending.length} suggestion{pending.length > 1 ? 's' : ''} pending your review
          </h3>
          {pending.map(s => (
            <div key={s.id} style={{ padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{s.threshold_type.replace(/_/g, ' ')}</span>
                <span className="mono" style={{ fontSize: 12 }}>
                  {formatAmount(s.current_value)} {'\u2192'} {formatAmount(s.suggested_value)}
                </span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.reason}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card" style={{ padding: 20, marginBottom: 24 }}>
        <h3 className="stitle" style={{ marginBottom: 16 }}>Current Thresholds</h3>
        {thresholds.length === 0 ? (
          <p style={{ color: 'var(--text-tertiary)' }}>No thresholds configured.</p>
        ) : (
          thresholds.map(t => (
            <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border-subtle)' }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 500 }}>{t.threshold_type.replace(/_/g, ' ')}</span>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Range: {formatAmount(t.min_allowed)} - {formatAmount(t.max_allowed)}
                </div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div className="mono" style={{ fontSize: 16, fontWeight: 600 }}>{formatAmount(t.current_value)}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>
                  Set by {t.set_by} {t.set_at ? `on ${new Date(t.set_at).toLocaleDateString()}` : ''}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {dashboard && (
        <div style={{ display: 'flex', gap: 16 }}>
          {[
            { label: 'Pending', value: dashboard.pending_count ?? 0, color: 'var(--amber)' },
            { label: 'Thresholds', value: dashboard.threshold_summary?.length ?? 0, color: 'var(--green)' },
            { label: 'Recent Decisions', value: dashboard.recent_decisions?.length ?? 0, color: 'var(--blue)' },
          ].map((m, i) => (
            <div key={i} className="card" style={{ padding: 12, textAlign: 'center', flex: 1 }}>
              <div className="stitle" style={{ marginBottom: 2, fontSize: 10 }}>{m.label}</div>
              <div className="mono" style={{ fontSize: 20, fontWeight: 600, color: m.color }}>{m.value}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
